import { useEffect, useState } from "react";
import { toast } from "sonner";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "../components/ui/card";
import { Button } from "../components/ui/button";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../components/ui/select";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "../components/ui/tabs";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "../components/ui/dialog";
import { Checkbox } from "../components/ui/checkbox";
import { useAuth } from "../App";
import { getArchivedSchoolYears, getArchivedStudents, getArchivedRoster, getArchivedAttendance, getArchivedAssessments, getArchivedIncidents, reenrollStudents, restoreArchivedYear } from "../lib/api";

export default function ArchivesPage() {
  const { isAdmin } = useAuth();
  const [schoolYears, setSchoolYears] = useState([]);
  const [selectedYear, setSelectedYear] = useState("");
  const [loading, setLoading] = useState(true);

  const [students, setStudents] = useState([]);
  const [roster, setRoster] = useState([]);
  const [attendance, setAttendance] = useState({ records: [], total: 0 });
  const [assessments, setAssessments] = useState({ records: [], total: 0 });
  const [incidents, setIncidents] = useState({ records: [], total: 0 });
  const [tabLoading, setTabLoading] = useState(false);
  const [selectedStudentId, setSelectedStudentId] = useState("all");

  const [restoreOpen, setRestoreOpen] = useState(false);
  const [restoreRunning, setRestoreRunning] = useState(false);
  const [restoreOpts, setRestoreOpts] = useState({ restore_attendance: true, restore_assessments: true, restore_incidents: false });

  const handleRestore = async () => {
    if (!selectedYear) return;
    setRestoreRunning(true);
    try {
      const res = await restoreArchivedYear(selectedYear, restoreOpts);
      const r = res.data.results || {};
      const skipped = r.skipped ? ` (${r.skipped} record(s) could not be matched and were skipped)` : "";
      toast.success(`Restored to live view — ${r.attendance_restored || 0} attendance, ${r.assessments_restored || 0} assessments${restoreOpts.restore_incidents ? `, ${r.incidents_restored || 0} incidents` : ""}. They now appear in student files and reports.${skipped}`);
      setRestoreOpen(false);
    } catch (err) {
      toast.error(err.response?.data?.detail || "Failed to restore archived data");
    } finally {
      setRestoreRunning(false);
    }
  };

  useEffect(() => {
    const fetchYears = async () => {
      try {
        const res = await getArchivedSchoolYears();
        setSchoolYears(res.data);
        if (res.data.length > 0) setSelectedYear(res.data[0].school_year);
      } catch { /* no archives yet */ }
      setLoading(false);
    };
    fetchYears();
  }, []);

  useEffect(() => {
    if (!selectedYear) return;
    const fetchYearData = async () => {
      setTabLoading(true);
      try {
        const [studRes, rosterRes, attRes, assRes, incRes] = await Promise.all([
          getArchivedStudents(selectedYear),
          getArchivedRoster(selectedYear),
          getArchivedAttendance(selectedYear, { limit: 200 }),
          getArchivedAssessments(selectedYear, { limit: 200 }),
          getArchivedIncidents(selectedYear, { limit: 200 }),
        ]);
        setStudents([...(studRes.data || [])].sort((a, b) => (a.last_name || "").localeCompare(b.last_name || "")));
        setRoster(rosterRes.data || []);
        setAttendance(attRes.data);
        setAssessments(assRes.data);
        setIncidents(incRes.data);
        setSelectedStudentId("all");
      } catch { toast.error("Failed to load archive data"); }
      setTabLoading(false);
    };
    fetchYearData();
  }, [selectedYear]);

  const handleDownloadReport = async () => {
    if (!selectedYear) return;
    try {
      toast.info("Generating End of Year Report...");
      const token = localStorage.getItem("token");
      const baseUrl = process.env.REACT_APP_BACKEND_URL;
      const res = await fetch(`${baseUrl}/api/archives/${selectedYear}/end-of-year-report`, { headers: { Authorization: `Bearer ${token}` } });
      if (!res.ok) throw new Error("Failed");
      const blob = await res.blob();
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a"); a.href = url; a.download = `End_of_Year_Report_${selectedYear}.pdf`; a.click();
      URL.revokeObjectURL(url);
      toast.success("Report downloaded!");
    } catch { toast.error("Failed to generate report. Make sure the year has archived data."); }
  };

  const handleFilterByStudent = async (studentId) => {
    setSelectedStudentId(studentId);
    if (!selectedYear) return;
    setTabLoading(true);
    try {
      const params = studentId !== "all" ? { student_id: studentId, limit: 500 } : { limit: 200 };
      const [attRes, assRes, incRes] = await Promise.all([
        getArchivedAttendance(selectedYear, params),
        getArchivedAssessments(selectedYear, params),
        getArchivedIncidents(selectedYear, params),
      ]);
      setAttendance(attRes.data);
      setAssessments(assRes.data);
      setIncidents(incRes.data);
    } catch { toast.error("Failed to filter data"); }
    setTabLoading(false);
  };

  if (loading) return <div className="flex items-center justify-center h-64"><div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary"></div></div>;

  const yearStats = schoolYears.find(y => y.school_year === selectedYear);

  // Name resolution: roster is the superset (archived snapshot + live-resolved students
  // referenced by this year's records), so partial / attendance-only years still show
  // real names instead of raw ids.
  const rosterById = new Map();
  roster.forEach(s => rosterById.set(s.id, s));
  students.forEach(s => { if (!rosterById.has(s.id)) rosterById.set(s.id, s); });
  const nameFor = (studentId, fallbackName) => {
    const s = rosterById.get(studentId);
    if (s) return `${s.first_name || ""} ${s.last_name || ""}`.trim() || fallbackName || studentId;
    return fallbackName || studentId;
  };
  // Students shown in the filter dropdown: full roster when the snapshot is empty.
  const filterStudents = (students.length ? students : roster);
  const isPartial = !!yearStats?.partial;

  return (
    <div className="space-y-6" data-testid="archives-page">
      <div>
        <h1 className="text-3xl font-bold text-[#00305E] tracking-tight">Archives</h1>
        <p className="text-muted-foreground mt-1">Browse historical school year data (read-only)</p>
      </div>

      {schoolYears.length === 0 ? (
        <Card>
          <CardContent className="py-12 text-center">
            <svg xmlns="http://www.w3.org/2000/svg" width="48" height="48" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" className="mx-auto mb-4 text-muted-foreground"><path d="m21 8-2 2-1.5-3.7A2 2 0 0 0 15.646 5H8.354a2 2 0 0 0-1.854 1.3L5 10 3 8"/><path d="M7 14h.01"/><path d="M17 14h.01"/><rect width="18" height="8" x="3" y="10" rx="2"/><path d="M5 18v2"/><path d="M19 18v2"/></svg>
            <p className="text-lg font-medium text-muted-foreground">No archived data yet</p>
            <p className="text-sm text-muted-foreground mt-2">When you run the New Year Wizard from Settings, previous year data will appear here.</p>
          </CardContent>
        </Card>
      ) : (
        <>
          {/* Year Selector */}
          <Card>
            <CardContent className="py-4">
              <div className="flex items-center gap-4">
                <div className="space-y-1">
                  <Label className="text-xs text-muted-foreground">School Year</Label>
                  <Select value={selectedYear} onValueChange={setSelectedYear}>
                    <SelectTrigger className="w-48" data-testid="archive-year-select"><SelectValue /></SelectTrigger>
                    <SelectContent>
                      {schoolYears.map(y => <SelectItem key={y.school_year} value={y.school_year}>{y.school_year}</SelectItem>)}
                    </SelectContent>
                  </Select>
                </div>
                {yearStats && (
                  <div className="flex gap-4 text-sm pt-4">
                    <span className="px-2 py-1 rounded bg-blue-50 text-blue-700">{yearStats.students} students</span>
                    <span className="px-2 py-1 rounded bg-green-50 text-green-700">{yearStats.attendance_records.toLocaleString()} attendance</span>
                    <span className="px-2 py-1 rounded bg-purple-50 text-purple-700">{yearStats.assessment_records.toLocaleString()} assessments</span>
                    <span className="px-2 py-1 rounded bg-red-50 text-red-700">{yearStats.incident_records} incidents</span>
                  </div>
                )}
                <div className="ml-auto flex items-end gap-3">
                  <div className="space-y-1">
                    <Label className="text-xs text-muted-foreground">Filter by Student</Label>
                    <Select value={selectedStudentId} onValueChange={handleFilterByStudent}>
                      <SelectTrigger className="w-56" data-testid="archive-student-filter"><SelectValue placeholder="All Students" /></SelectTrigger>
                      <SelectContent>
                        <SelectItem value="all">All Students</SelectItem>
                        {filterStudents.map(s => <SelectItem key={s.id} value={s.id}>{s.last_name || ""}{s.last_name ? ", " : ""}{s.preferred_name || s.first_name}{s.graduation_status === "graduated" ? " (Graduated)" : s.graduation_status === "left" ? " (Left)" : ""}</SelectItem>)}
                      </SelectContent>
                    </Select>
                  </div>
                  <Button variant="outline" size="sm" className="shrink-0" onClick={handleDownloadReport} data-testid="download-eoy-report-btn">
                    <svg xmlns="http://www.w3.org/2000/svg" width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className="mr-1"><path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4"/><polyline points="7 10 12 15 17 10"/><line x1="12" y1="15" x2="12" y2="3"/></svg>
                    End of Year Report
                  </Button>
                  {isAdmin() && (
                    <Button size="sm" className="shrink-0 bg-emerald-600 hover:bg-emerald-700" onClick={() => setRestoreOpen(true)} data-testid="open-restore-btn">
                      <svg xmlns="http://www.w3.org/2000/svg" width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className="mr-1"><path d="M3 12a9 9 0 1 0 9-9 9.75 9.75 0 0 0-6.74 2.74L3 8"/><path d="M3 3v5h5"/><path d="M12 7v5l4 2"/></svg>
                      Restore to Live
                    </Button>
                  )}
                </div>
              </div>
            </CardContent>
          </Card>

          {/* Partial (attendance-only) year notice */}
          {isPartial && (
            <div
              className="flex items-start gap-3 rounded-lg border border-amber-300 bg-amber-50 px-4 py-3 text-sm text-amber-800"
              data-testid="archive-partial-banner"
            >
              <svg xmlns="http://www.w3.org/2000/svg" width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className="mt-0.5 shrink-0"><path d="m21.73 18-8-14a2 2 0 0 0-3.48 0l-8 14A2 2 0 0 0 4 21h16a2 2 0 0 0 1.73-3Z"/><line x1="12" y1="9" x2="12" y2="13"/><line x1="12" y1="17" x2="12.01" y2="17"/></svg>
              <div>
                <p className="font-semibold">{selectedYear} was archived as records only — no student snapshot was captured.</p>
                <p className="mt-0.5">This usually happens with an attendance-only archive (Semester Transition) or a historical import. Student names below are resolved from your current student files, so anyone who has since left may show as unavailable.</p>
              </div>
            </div>
          )}

          {/* Data Tabs */}
          <Tabs defaultValue="students" className="space-y-4">
            <TabsList>
              <TabsTrigger value="students" data-testid="archive-tab-students">Students ({students.length})</TabsTrigger>
              <TabsTrigger value="attendance" data-testid="archive-tab-attendance">Attendance ({attendance.total})</TabsTrigger>
              <TabsTrigger value="assessments" data-testid="archive-tab-assessments">Assessments ({assessments.total})</TabsTrigger>
              <TabsTrigger value="incidents" data-testid="archive-tab-incidents">Incidents ({incidents.total})</TabsTrigger>
            </TabsList>

            <TabsContent value="students">
              <Card>
                <CardHeader><CardTitle>Archived Students — {selectedYear}</CardTitle></CardHeader>
                <CardContent>
                  {students.length === 0 ? <p className="text-center py-8 text-muted-foreground" data-testid="archive-no-students">No student records for this year. If this year has attendance or other records, their names are resolved from your current student files in the other tabs.</p> : (
                    <div className="overflow-x-auto">
                      <table className="w-full text-sm" data-testid="archive-students-table">
                        <thead><tr className="border-b bg-muted"><th className="p-2 text-left">Name</th><th className="p-2 text-left">Grade</th><th className="p-2 text-left">District</th><th className="p-2 text-left">Status</th><th className="p-2 text-left">Advisory</th><th className="p-2 text-right">Actions</th></tr></thead>
                        <tbody>
                          {students.map(s => (
                            <tr key={s.id} className="border-b hover:bg-muted/50">
                              <td className="p-2 font-medium">{s.first_name} {s.last_name}</td>
                              <td className="p-2">{s.grade || "-"}</td>
                              <td className="p-2">{s.school_district || "-"}</td>
                              <td className="p-2">
                                {s.graduation_status === "graduated" ? <span className="px-2 py-0.5 rounded text-xs bg-purple-100 text-purple-700">Graduated</span> :
                                 s.graduation_status === "left" ? <span className="px-2 py-0.5 rounded text-xs bg-red-100 text-red-700">Left</span> :
                                 <span className="px-2 py-0.5 rounded text-xs bg-green-100 text-green-700">Returning</span>}
                              </td>
                              <td className="p-2 text-muted-foreground">{s.advisory_group || "-"}</td>
                              <td className="p-2 text-right">
                                {(s.graduation_status === "graduated" || s.graduation_status === "left") && (
                                  <Button variant="ghost" size="sm" className="h-7 px-2 text-xs text-blue-600 hover:text-blue-800" data-testid={`reenroll-${s.id}`}
                                    onClick={async () => {
                                      if (!window.confirm(`Re-enroll ${s.first_name} ${s.last_name}? This will create a fresh record with no attendance/assessment history.`)) return;
                                      try { await reenrollStudents([s.id]); toast.success(`${s.first_name} re-enrolled`); const res = await getArchivedStudents(selectedYear); setStudents(res.data); } catch { toast.error("Failed to re-enroll"); }
                                    }}>
                                    Re-enroll
                                  </Button>
                                )}
                              </td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>
                  )}
                </CardContent>
              </Card>
            </TabsContent>

            <TabsContent value="attendance">
              <Card>
                <CardHeader><CardTitle>Archived Attendance — {selectedYear}</CardTitle><CardDescription>{attendance.total.toLocaleString()} records{selectedStudentId !== "all" ? " (filtered)" : ""}</CardDescription></CardHeader>
                <CardContent>
                  {tabLoading ? <p className="text-center py-8 text-muted-foreground">Loading...</p> :
                   attendance.records.length === 0 ? <p className="text-center py-8 text-muted-foreground">No attendance records</p> : (
                    <div className="overflow-x-auto max-h-96 overflow-y-auto">
                      <table className="w-full text-sm">
                        <thead className="sticky top-0 bg-muted"><tr><th className="p-2 text-left">Date</th><th className="p-2 text-left">Student</th><th className="p-2 text-left">Status</th><th className="p-2 text-left">Check In</th><th className="p-2 text-left">Check Out</th></tr></thead>
                        <tbody>
                          {attendance.records.map((r, i) => {
                            return (
                              <tr key={r.id ?? `att-${i}`} className="border-b">
                                <td className="p-2">{r.date}</td>
                                <td className="p-2 font-medium">{nameFor(r.student_id, r.student_name)}</td>
                                <td className="p-2"><span className={`px-2 py-0.5 rounded text-xs ${r.status === "present" ? "bg-green-100 text-green-700" : r.status === "tardy" ? "bg-amber-100 text-amber-700" : "bg-red-100 text-red-700"}`}>{r.status}</span></td>
                                <td className="p-2 text-muted-foreground">{r.check_in_time || "-"}</td>
                                <td className="p-2 text-muted-foreground">{r.check_out_time || "-"}</td>
                              </tr>
                            );
                          })}
                        </tbody>
                      </table>
                    </div>
                  )}
                </CardContent>
              </Card>
            </TabsContent>

            <TabsContent value="assessments">
              <Card>
                <CardHeader><CardTitle>Archived Assessments — {selectedYear}</CardTitle><CardDescription>{assessments.total.toLocaleString()} records{selectedStudentId !== "all" ? " (filtered)" : ""}</CardDescription></CardHeader>
                <CardContent>
                  {tabLoading ? <p className="text-center py-8 text-muted-foreground">Loading...</p> :
                   assessments.records.length === 0 ? <p className="text-center py-8 text-muted-foreground">No assessment records</p> : (
                    <div className="overflow-x-auto max-h-96 overflow-y-auto">
                      <table className="w-full text-sm">
                        <thead className="sticky top-0 bg-muted"><tr><th className="p-2 text-left">Date</th><th className="p-2 text-left">Student</th><th className="p-2 text-left">Class</th><th className="p-2 text-center">Participation</th><th className="p-2 text-center">Behavior</th><th className="p-2 text-center">Engagement</th></tr></thead>
                        <tbody>
                          {assessments.records.map((r, i) => {
                            return (
                              <tr key={r.id ?? `ass-${i}`} className="border-b">
                                <td className="p-2">{r.date}</td>
                                <td className="p-2 font-medium">{nameFor(r.student_id, r.student_name)}</td>
                                <td className="p-2">{r.class_name || r.group_type || "-"}</td>
                                <td className="p-2 text-center">{r.participation_level || r.attendance_level || "-"}</td>
                                <td className="p-2 text-center">{r.behavior_level || "-"}</td>
                                <td className="p-2 text-center">{r.engagement_level || "-"}</td>
                              </tr>
                            );
                          })}
                        </tbody>
                      </table>
                    </div>
                  )}
                </CardContent>
              </Card>
            </TabsContent>

            <TabsContent value="incidents">
              <Card>
                <CardHeader><CardTitle>Archived Incidents — {selectedYear}</CardTitle><CardDescription>{incidents.total} records{selectedStudentId !== "all" ? " (filtered)" : ""}</CardDescription></CardHeader>
                <CardContent>
                  {tabLoading ? <p className="text-center py-8 text-muted-foreground">Loading...</p> :
                   incidents.records.length === 0 ? <p className="text-center py-8 text-muted-foreground">No incident records</p> : (
                    <div className="space-y-3">
                      {incidents.records.map((r, i) => {
                        return (
                          <div key={r.id ?? `inc-${i}`} className="p-3 border rounded-lg">
                            <div className="flex items-center gap-2 mb-1">
                              <span className={`px-2 py-0.5 rounded text-xs font-medium ${r.type === "incident" ? "bg-red-100 text-red-700" : r.type === "accident" ? "bg-orange-100 text-orange-700" : "bg-blue-100 text-blue-700"}`}>{r.type || "report"}</span>
                              <span className="text-sm font-medium">{nameFor(r.student_id, r.student_name)}</span>
                              <span className="text-xs text-muted-foreground ml-auto">{r.date || r.created_at?.split("T")[0]}</span>
                            </div>
                            {r.description && <p className="text-sm text-muted-foreground">{r.description.substring(0, 200)}{r.description.length > 200 ? "..." : ""}</p>}
                          </div>
                        );
                      })}
                    </div>
                  )}
                </CardContent>
              </Card>
            </TabsContent>
          </Tabs>
        </>
      )}

      <Dialog open={restoreOpen} onOpenChange={setRestoreOpen}>
        <DialogContent className="max-w-md" data-testid="restore-dialog">
          <DialogHeader>
            <DialogTitle className="text-emerald-700">Restore {selectedYear} to Live View</DialogTitle>
          </DialogHeader>
          <div className="space-y-3 pt-1 text-sm">
            <p>This copies the archived records for <strong>{selectedYear}</strong> back into your live student files and reports. It&apos;s a <strong>safe merge</strong> — nothing is deleted and running it more than once won&apos;t create duplicates.</p>
            <div className="space-y-2 rounded-lg border p-3 bg-muted/30">
              <label className="flex items-center gap-2 cursor-pointer">
                <Checkbox checked={restoreOpts.restore_attendance} onCheckedChange={(c) => setRestoreOpts((p) => ({ ...p, restore_attendance: !!c }))} data-testid="restore-opt-attendance" />
                <span>Attendance ({yearStats?.attendance_records?.toLocaleString() || 0} records)</span>
              </label>
              <label className="flex items-center gap-2 cursor-pointer">
                <Checkbox checked={restoreOpts.restore_assessments} onCheckedChange={(c) => setRestoreOpts((p) => ({ ...p, restore_assessments: !!c }))} data-testid="restore-opt-assessments" />
                <span>Assessments ({yearStats?.assessment_records?.toLocaleString() || 0} records)</span>
              </label>
              <label className="flex items-center gap-2 cursor-pointer">
                <Checkbox checked={restoreOpts.restore_incidents} onCheckedChange={(c) => setRestoreOpts((p) => ({ ...p, restore_incidents: !!c }))} data-testid="restore-opt-incidents" />
                <span>Incident reports ({yearStats?.incident_records || 0} records)</span>
              </label>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setRestoreOpen(false)}>Cancel</Button>
            <Button className="bg-emerald-600 hover:bg-emerald-700" onClick={handleRestore} disabled={restoreRunning || (!restoreOpts.restore_attendance && !restoreOpts.restore_assessments && !restoreOpts.restore_incidents)} data-testid="confirm-restore-btn">
              {restoreRunning ? "Restoring..." : "Restore to Live"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}

function Label({ children, ...props }) {
  return <label className="text-sm font-medium" {...props}>{children}</label>;
}
